/**
 * Base Pet metadata contract — the DFE Inventory arche's own copy.
 *
 * A Base Pet is an NFT whose entire behaviour lives in its `metadata` JSONB
 * blob. The Crafting arche owns that contract: `craftBasePet` in
 * `services/api-service/src/services/crafting-service.ts` writes the blob when
 * a player crafts a pet, `craftPetModify` mutates it, and the caps plus the
 * `BasePetMetadata` interface and the `isPetFullyImproved` gate live in
 * `packages/db-types/src/crafting.ts`.
 *
 * This module DELIBERATELY REPEATS that contract rather than importing it:
 *
 *   - The client package has no `@fias/db-types` dependency (it is a
 *     server-side package), so the canonical constants are not reachable here.
 *   - Making them reachable would mean either adding LitCraft domain knowledge
 *     to a general-Fias package, or giving the DFE Inventory arche a runtime
 *     dependency on the Crafting arche. Both are a large architectural price
 *     for what is an admin-only convenience on one dialog.
 *
 * So the duplication is the deliberate choice, and `base-pet-metadata.parity.test.ts`
 * is the thing that stops it rotting: it reads the two upstream files as text
 * and fails CI if a field or a cap moves. If that test fails, re-sync THIS file
 * — do not weaken the test.
 *
 * Scope note: `LitPet - *` items carry a completely different genotype blob and
 * are not handled here.
 */

/** Item-definition name prefix that identifies a Base Pet, e.g. `Base Pet - Honricle`. */
export const BASE_PET_NAME_PREFIX = 'Base Pet - ';

/**
 * Magical power floor. `parseMagicTile` in the crafting service defaults to 1
 * when no tile colour matches, so 1 is the weakest pet the game can produce.
 */
export const PET_MIN_MAGICAL_POWER = 1;

/** Mirrors `PET_MAX_MAGICAL_POWER` (= `MAGIC_TILE_COLORS.length`) in `@fias/db-types`. */
export const PET_MAX_MAGICAL_POWER = 7;

/** Mirrors `PET_MAX_FOOD_COUNT` in `@fias/db-types`. */
export const PET_MAX_FOOD_COUNT = 10;

/** Mirrors `PET_MAX_MEDICINE_COUNT` in `@fias/db-types`. */
export const PET_MAX_MEDICINE_COUNT = 3;

/**
 * Mirrors `BasePetMetadata` in `packages/db-types/src/crafting.ts`.
 *
 * Field order matters here: `formatBasePetMetadata` serialises in declaration
 * order so the JSON an admin sees reads the same way as the literal in
 * `craftBasePet`, which makes the two diffable by eye during review.
 */
export interface BasePetMetadata {
  /** Short item type, e.g. `Pet - Honricle` (the `Base ` prefix is stripped). */
  tType: string;
  /** Bare species name, e.g. `Honricle`. */
  species: string;
  /** `PET_MIN_MAGICAL_POWER`..`PET_MAX_MAGICAL_POWER`. */
  magicalPower: number;
  hasClothes: boolean;
  hasHat: boolean;
  hasOrnateClothing: boolean;
  /** `0`..`PET_MAX_FOOD_COUNT`. */
  foodCount: number;
  /** `0`..`PET_MAX_MEDICINE_COUNT`. */
  medicineCount: number;
  hasHouse: boolean;
}

/** True when an item-definition name identifies a Base Pet. */
export function isBasePetDefinitionName(name: string | null | undefined): boolean {
  return typeof name === 'string' && name.startsWith(BASE_PET_NAME_PREFIX);
}

/**
 * Build the metadata blob for a Base Pet of the given item-definition name.
 *
 * With no options this reproduces a freshly crafted pet: every improvement
 * unspent and magical power at the floor. With `maxed`, it reproduces a fully
 * improved pet — every cap reached and every improvement flag set, which is
 * what `isPetFullyImproved` (the LitPet breeding gate) requires.
 *
 * `hasClothes` and `hasOrnateClothing` are independent booleans in
 * `craftPetModify`, not a single either/or choice, so a maxed pet sets both.
 *
 * @throws if `definitionName` is not a Base Pet. The derivation below is only
 * meaningful for the `Base Pet - <species>` shape — handed anything else it
 * would happily emit `{ tType: 'Grass', species: 'Grass' }` and that blob would
 * be minted onto a real NFT. Guard with `isBasePetDefinitionName` first.
 */
export function buildBasePetMetadata(
  definitionName: string,
  options: { maxed?: boolean } = {},
): BasePetMetadata {
  if (!isBasePetDefinitionName(definitionName)) {
    throw new Error(
      `Not a Base Pet definition name: "${definitionName}" (expected the "${BASE_PET_NAME_PREFIX}" prefix)`,
    );
  }

  const maxed = options.maxed === true;

  // Same derivation as `craftBasePet`: strip `Base ` for the short item type,
  // then strip `Pet - ` again for the bare species.
  const tType = definitionName.replace(/^Base /, '');
  const species = tType.replace(/^Pet - /, '');

  return {
    tType,
    species,
    magicalPower: maxed ? PET_MAX_MAGICAL_POWER : PET_MIN_MAGICAL_POWER,
    hasClothes: maxed,
    hasHat: maxed,
    hasOrnateClothing: maxed,
    foodCount: maxed ? PET_MAX_FOOD_COUNT : 0,
    medicineCount: maxed ? PET_MAX_MEDICINE_COUNT : 0,
    hasHouse: maxed,
  };
}

/** Serialise a Base Pet blob for the admin metadata textarea. */
export function formatBasePetMetadata(metadata: BasePetMetadata): string {
  return JSON.stringify(metadata, null, 2);
}

/**
 * Report when a metadata blob claims a different species than the definition
 * about to be minted, or `null` when they agree.
 *
 * This exists because the mint form deliberately preserves hand-edited
 * metadata across a definition change, which makes it possible to carry a
 * blob for one species onto a different pet — minting, say, a Sloog NFT whose
 * `species` still reads `Honricle`. Nothing downstream would complain: the
 * mining-bonus reader keys off the metadata, not the definition, and LitPet
 * breeding resolves an unknown species to default genes silently.
 *
 * Both legacy spellings (`Pet - X`, `Base Pet - X`) are normalised away before
 * comparing, matching the mining reader's regex — a legacy blob on the right
 * pet is not a mismatch. A blob with no species at all is not reported here;
 * that is a hand-authored blob, not a stale one.
 */
export function findBasePetSpeciesMismatch(
  definitionName: string,
  metadata: Record<string, unknown>,
): { expected: string; actual: string } | null {
  if (!isBasePetDefinitionName(definitionName)) return null;

  const raw = metadata.species ?? metadata.tType;
  if (typeof raw !== 'string' || raw === '') return null;

  const expected = buildBasePetMetadata(definitionName).species;
  const actual = raw.replace(/^(?:Base )?Pet - /, '');

  return actual === expected ? null : { expected, actual };
}
